/**
 * The outgoing-message queue.
 *
 * Connect is online-only: a message is only ever stored on the server, never
 * kept locally to be synced later. What it does do is ride out a short drop —
 * a laptop waking, a server restart, wifi changing — so a message typed during
 * one is not lost. Each send is attempted, and a failure that looks transient
 * (no response at all, a 5xx, a 408 or 429) is retried on a backoff and again
 * the moment the socket reconnects, up to `maxAttempts`. After that the send is
 * reported failed and the bubble offers "Retry", which enqueues it afresh.
 *
 * A 4xx is the server's answer, not a dropped line — retrying cannot change it,
 * so it fails at once with the server's reason.
 *
 * Retrying is safe because the message endpoint is idempotent on
 * (channelId, clientMessageId): an attempt that landed but lost its response
 * returns the original message the second time instead of creating another.
 *
 * Held in memory only, deliberately. Closing the tab drops whatever has not
 * been sent, exactly as it drops a half-typed draft.
 */

export interface SendQueueOptions {
  /** Attempts before a send is reported failed, counting the first. */
  maxAttempts?: number;
  /** Delay before retry n (1-based), in ms. Clamped to the last entry. */
  backoffMs?: readonly number[];
}

interface Entry {
  attempt: () => Promise<unknown>;
  onSent: (result: unknown) => void;
  onFailed: (error: unknown) => void;
  attempts: number;
  timer: ReturnType<typeof setTimeout> | undefined;
  inFlight: boolean;
}

export const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000] as const;

/** Whether a failure is worth another go: no response, or a server that is
 *  struggling rather than refusing. Anything with a 4xx status is final. */
export function isRetryable(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== "number") return true;
  return status >= 500 || status === 408 || status === 429;
}

export class SendQueue {
  private entries = new Map<string, Entry>();
  private readonly maxAttempts: number;
  private readonly backoffMs: readonly number[];

  constructor(options: SendQueueOptions = {}) {
    this.maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    this.backoffMs = options.backoffMs ?? DEFAULT_BACKOFF_MS;
  }

  /**
   * Send something, retrying transient failures. `id` identifies the send —
   * the optimistic row's clientMessageId — so enqueueing the same id again
   * (a "Retry" tap) replaces the old entry and starts a fresh count.
   */
  enqueue<T>(
    id: string,
    attempt: () => Promise<T>,
    callbacks: { onSent: (result: T) => void; onFailed: (error: unknown) => void },
  ): void {
    this.cancel(id);
    const entry: Entry = {
      attempt,
      onSent: callbacks.onSent as (result: unknown) => void,
      onFailed: callbacks.onFailed,
      attempts: 0,
      timer: undefined,
      inFlight: false,
    };
    this.entries.set(id, entry);
    this.run(id, entry);
  }

  /**
   * The connection is back: retry every waiting send now instead of at the end
   * of its backoff. A send already in flight is left alone.
   */
  flush(): void {
    for (const [id, entry] of this.entries) {
      if (entry.inFlight) continue;
      clearTimeout(entry.timer);
      this.run(id, entry);
    }
  }

  /** Stop retrying one send without reporting anything. */
  cancel(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    this.entries.delete(id);
  }

  /** Stop everything — the owning screen is going away. */
  dispose(): void {
    for (const entry of this.entries.values()) clearTimeout(entry.timer);
    this.entries.clear();
  }

  /** How many sends are still waiting or in flight. */
  get size(): number {
    return this.entries.size;
  }

  private run(id: string, entry: Entry): void {
    entry.timer = undefined;
    entry.inFlight = true;
    entry.attempts += 1;
    entry.attempt().then(
      (result) => {
        // Cancelled or replaced while in flight: whoever did that owns the row now.
        if (this.entries.get(id) !== entry) return;
        this.entries.delete(id);
        entry.onSent(result);
      },
      (error: unknown) => {
        if (this.entries.get(id) !== entry) return;
        entry.inFlight = false;
        if (!isRetryable(error) || entry.attempts >= this.maxAttempts) {
          this.entries.delete(id);
          entry.onFailed(error);
          return;
        }
        const delays = this.backoffMs;
        const delay = delays[Math.min(entry.attempts - 1, delays.length - 1)] ?? 0;
        entry.timer = setTimeout(() => this.run(id, entry), delay);
      },
    );
  }
}
